import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type {
  AddContainerRequest,
  ContainerAllocationDto,
  ContainerDetailDto,
  ContainerSummaryDto
} from "@/types/api";

/**
 * TanStack Query hooks for the /api/containers surface. Mutations
 * invalidate the right keys so a successful POST surfaces in the list
 * without manual refetch — same for PATCH on allocation.
 */

export function useContainers() {
  return useQuery({
    queryKey: ["containers"],
    queryFn: ({ signal }) => api.get<ContainerSummaryDto[]>("/api/containers", signal)
  });
}

export function useContainer(id: number | undefined) {
  return useQuery({
    queryKey: ["containers", id],
    enabled: typeof id === "number",
    queryFn: ({ signal }) =>
      api.get<ContainerDetailDto>(`/api/containers/${id}`, signal)
  });
}

export function useAddContainer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AddContainerRequest) =>
      api.post<ContainerDetailDto>("/api/containers", body),
    onSuccess: detail => {
      // Bust the list AND pre-seed the detail query so the dashboard
      // navigates instantly to the new container's page.
      qc.invalidateQueries({ queryKey: ["containers"] });
      qc.setQueryData(["containers", detail.id], detail);
      // Account pool numbers change — refresh the dashboard hero too.
      qc.invalidateQueries({ queryKey: ["accounts", "me"] });
    }
  });
}

/**
 * Permanently removes a domain: detaches/deletes its 20i hosting in real
 * time, then the container. Irreversible — the UI must hard-confirm first.
 */
export function useDeleteContainer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (containerId: number) => api.delete(`/api/containers/${containerId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["containers"] });
      qc.invalidateQueries({ queryKey: ["accounts", "me"] });
    }
  });
}

export function useUpdateAllocation(containerId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (allocation: ContainerAllocationDto) =>
      api.patch<ContainerAllocationDto>(
        `/api/containers/${containerId}/allocation`,
        allocation
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["containers", containerId] });
      qc.invalidateQueries({ queryKey: ["containers"] });
      qc.invalidateQueries({ queryKey: ["accounts", "me"] });
    }
  });
}
